import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  List,
  ListItem,
  Typography,
  Paper,
  useTheme,
  Chip,
} from '@mui/material';
import { Search, Clear } from '@mui/icons-material';
import { tokens } from '../theme';

const SearchBar = ({ 
  playerPool,
  draftedIds,
  customDraftAssignments,
  manuallyDraftedIds,
  onHighlightPlayer,
  onPlayerSelected,
  positionColors,
  autoFocus = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [searchTerm, setSearchTerm] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchChange = useCallback((event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setShowResults(true);
    onHighlightPlayer(value); // Trigger highlighting
  }, [onHighlightPlayer]);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setShowResults(false);
    onHighlightPlayer(''); // Clear highlighting
  }, [onHighlightPlayer]);

  const getPlayerStatus = (player) => {
    const playerId = player.sleeper_id || player.id;
    if (draftedIds.includes(player.sleeper_id)) {
      return { label: 'Drafted', color: colors.grey[500] };
    }
    if (manuallyDraftedIds.has(player.sleeper_id)) {
      return { label: 'Drafted', color: colors.grey[500] };
    }
    if (customDraftAssignments[playerId]) {
      const assignment = customDraftAssignments[playerId];
      return { 
        label: `Round ${assignment.round}, Team ${assignment.position}`, 
        color: colors.blueAccent[500] 
      };
    }
    return { label: 'Available', color: colors.greenAccent[500] };
  };

  const filteredPlayers = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return []; // Don't search for single characters
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    return playerPool.filter(player => {
      const playerName = player.name?.toLowerCase() || '';
      const playerInfo = `${player.position} ${player.team || 'FA'}`.toLowerCase();
      return playerName.includes(lowerSearchTerm) || playerInfo.includes(lowerSearchTerm);
    }).slice(0, 20); // Limit results to first 20 to prevent huge lists
  }, [searchTerm, playerPool]);

  return (
    <Box
      ref={searchRef}
      sx={{
        position: 'relative',
        width: { xs: '100%', sm: '160px', md: '200px', lg: '240px', xl: '280px' },
      }}
    >
      <TextField
        fullWidth
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search players..."
        variant="outlined"
        size="small"
        onFocus={() => setShowResults(true)}
        autoFocus={autoFocus}
        type="search"
        inputProps={{ inputMode: 'search' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{ color: colors.grey[300] }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <Clear
                sx={{ 
                  color: colors.grey[300],
                  cursor: 'pointer',
                  '&:hover': { color: colors.grey[100] }
                }}
                onClick={clearSearch}
              />
            </InputAdornment>
          ),
          sx: {
            backgroundColor: colors.primary[400],
            borderRadius: '4px',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.grey[700],
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.grey[500],
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.grey[100],
            },
            color: colors.grey[100],
            '& input': {
              fontSize: '16px', // prevent iOS zoom on focus
            },
          }
        }}
      />
      
      {showResults && searchTerm && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 1,
            maxHeight: { xs: '50vh', sm: '400px' },
            overflow: 'auto',
            backgroundColor: colors.primary[400],
            border: `1px solid ${colors.grey[700]}`,
            zIndex: 1000,
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: colors.primary[400],
            },
            '&::-webkit-scrollbar-thumb': {
              background: colors.grey[700],
              borderRadius: '4px',
            },
          }}
        >
          <List disablePadding>
            {filteredPlayers.length === 0 ? (
              <ListItem>
                <Typography sx={{ color: colors.grey[300] }}>
                  No players found
                </Typography>
              </ListItem>
            ) : (
              filteredPlayers.map((player) => {
                const status = getPlayerStatus(player);
                return (
                  <ListItem
                    key={player.id}
                    sx={{
                      borderBottom: `1px solid ${colors.grey[700]}`,
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': {
                        backgroundColor: colors.primary[500],
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => {
                      onHighlightPlayer(player.name);
                      if (onPlayerSelected) {
                        onPlayerSelected(player.name);
                      }
                      setShowResults(false);
                    }}
                  >
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ color: colors.grey[100], fontWeight: 'bold' }}>
                          {player.name}
                        </Typography>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            backgroundColor: status.color,
                            color: colors.grey[900],
                            fontSize: '10px',
                            height: '20px',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          label={player.position}
                          size="small"
                          sx={{
                            backgroundColor: positionColors[player.position],
                            color: colors.grey[900],
                            fontSize: '10px',
                            height: '18px',
                          }}
                        />
                        <Typography sx={{ color: colors.grey[300], fontSize: '12px' }}>
                          {player.team || 'FA'}
                        </Typography>
                        <Typography sx={{ color: colors.grey[300], fontSize: '12px' }}>
                          ECR: {player.ovr_rank_ecr || '?'}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                );
              })
            )}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar; 